"use client"

import * as React from "react"
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core"
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { ChevronRight, GripVertical, Pin, Search, type LucideIcon } from "lucide-react"
import { Link, useLocation } from "react-router-dom"

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu"
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarInput,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "./ui/sidebar"
import { cn } from "@/lib/utils"
import { useSidebarNavStore } from "@/client/stores/sidebar-nav"

/** Sidebar supports four levels: 1 top-level, 2–4 nested. */
const MAX_DEPTH = 4

/** Top-level section headers with `url: '#'` are stored as `nav:Business`. */
export const NAV_SECTION_PIN_PREFIX = "nav:"

interface NavItem {
  title: string
  url: string
  icon?: LucideIcon
  isActive?: boolean
  items?: NavItem[]
  /** For pinned favorites: the exact pin string that resolved to this item. */
  pinKey?: string
}

/** Only first-level sidebar items are pinnable. */
export function navItemPinKey(item: Pick<NavItem, "title" | "url">): string {
  if (item.url && item.url !== "#") return item.url
  return `${NAV_SECTION_PIN_PREFIX}${item.title}`
}

export function canPinNavItem(item: NavItem): boolean {
  const href = firstNavigableUrl(item)
  return Boolean(href && href !== "#")
}

export function findNavItemByPin<T extends { title: string; url: string }>(
  items: T[],
  pin: string,
): T | null {
  for (const item of items) {
    if (navItemPinKey(item) === pin || item.title === pin) return item
  }
  return null
}

/** Leaf match: query links require exact param match; bare path matches only when filter params are absent. */
function leafMatches(url: string, pathname: string, search = ""): boolean {
  if (!url || url === "#") return false
  const [urlPath, urlQuery = ""] = url.split("?")
  if (pathname !== urlPath) {
    // Pool page should stay lit on a prospect record, not on Search.
    if (urlPath === '/admin/sales/prospects') {
      if (!pathname.startsWith(`${urlPath}/`)) return false
      const rest = pathname.slice(urlPath.length + 1)
      return rest !== 'search' && rest !== 'overview'
    }
    if (urlPath === '/admin/sales/outreach' && pathname.startsWith('/admin/sales/r/')) {
      return true
    }
    // Section roots must not match every nested child route
    if (
      urlPath === '/admin' ||
      urlPath === '/admin/sales' ||
      urlPath === '/admin/payments' ||
      urlPath === '/admin/payments/orders' ||
      urlPath === '/admin/communications/messaging' ||
      urlPath === '/admin/communications/calling' ||
      urlPath === '/admin/communications/notifications' ||
      urlPath === '/admin/marketing/campaigns' ||
      urlPath === '/admin/marketing/plays' ||
      urlPath === '/admin/marketing/radar' ||
      urlPath === '/admin/scheduler' ||
      urlPath === '/admin/cloud' ||
      urlPath === '/admin/workers' ||
      urlPath === '/admin/rate-limits'
    ) {
      return false
    }
    return pathname.startsWith(`${urlPath}/`) && !urlQuery
  }
  const current = new URLSearchParams(search.startsWith("?") ? search : search ? `?${search}` : "")
  if (!urlQuery) {
    return !current.get("status") && !current.get("tab")
  }
  const expected = new URLSearchParams(urlQuery)
  for (const [key, value] of expected.entries()) {
    if (current.get(key) !== value) return false
  }
  return true
}

function hasActiveDescendant(item: NavItem, pathname: string, search = ""): boolean {
  if (leafMatches(item.url, pathname, search)) return true
  return item.items?.some((child) => hasActiveDescendant(child, pathname, search)) ?? false
}

/**
 * The URL a nav item should navigate to when clicked. Section headers carry
 * `url: '#'` (or empty) and only make sense as a container, so we fall through
 * to their first real descendant leaf — otherwise a pinned section is a dead
 * link that does nothing on click.
 */
function firstNavigableUrl(item: NavItem): string {
  if (item.url && item.url !== "#") return item.url
  const queue = [...(item.items ?? [])]
  while (queue.length) {
    const child = queue.shift()!
    if (child.url && child.url !== "#") return child.url
    if (child.items?.length) queue.push(...child.items)
  }
  return item.url || "#"
}

function filterNavItems(items: NavItem[], query: string): NavItem[] {
  const q = query.trim().toLowerCase()
  if (!q) return items

  const filterItem = (item: NavItem): NavItem | null => {
    const titleMatch = item.title.toLowerCase().includes(q)
    const filteredChildren = item.items
      ?.map(filterItem)
      .filter((child): child is NavItem => child !== null)

    if (titleMatch) {
      // Keep full subtree when the parent title matches
      return item
    }
    if (filteredChildren && filteredChildren.length > 0) {
      return { ...item, items: filteredChildren }
    }
    return null
  }

  return items.map(filterItem).filter((item): item is NavItem => item !== null)
}

function NestedSub({
  depth,
  children,
}: {
  depth: number
  children: React.ReactNode
}) {
  return (
    <SidebarMenuSub
      className={cn(
        depth >= 3 && "mx-0 translate-x-0 border-l-0 px-0 pl-3"
      )}
    >
      {children}
    </SidebarMenuSub>
  )
}

function NavMenuItem({
  item,
  depth,
  pathname,
  search,
  forceOpen,
}: {
  item: NavItem
  depth: number
  pathname: string
  search: string
  forceOpen?: boolean
}) {
  const hasChildren = Boolean(item.items && item.items.length > 0)
  const canNest = hasChildren && depth < MAX_DEPTH
  const isBranchActive = hasActiveDescendant(item, pathname, search)
  const isLeafActive = leafMatches(item.url, pathname, search)
  const [open, setOpen] = React.useState(Boolean(item.isActive || isBranchActive || forceOpen))

  React.useEffect(() => {
    if (isBranchActive || forceOpen) setOpen(true)
  }, [isBranchActive, forceOpen, pathname, search])

  if (!canNest) {
    return (
      <SidebarMenuSubItem>
        <SidebarMenuSubButton
          asChild
          isActive={isLeafActive}
          className={cn("py-5", depth >= 4 && "text-xs")}
        >
          <Link to={item.url}>
            {item.icon && <item.icon />}
            <span>{item.title}</span>
          </Link>
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
    )
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen} asChild>
      <SidebarMenuSubItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuSubButton
            className={cn("py-5", depth >= 3 && "text-xs")}
            isActive={isBranchActive && !isLeafActive}
          >
            {item.icon && <item.icon />}
            <span>{item.title}</span>
            <ChevronRight
              className={cn(
                "ml-auto transition-transform duration-200",
                open && "rotate-90"
              )}
            />
          </SidebarMenuSubButton>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <NestedSub depth={depth + 1}>
            {item.items?.map((subItem) => (
              <NavMenuItem
                key={subItem.title}
                item={subItem}
                depth={depth + 1}
                pathname={pathname}
                search={search}
                forceOpen={forceOpen}
              />
            ))}
          </NestedSub>
        </CollapsibleContent>
      </SidebarMenuSubItem>
    </Collapsible>
  )
}

function PinButton({
  pinKey,
  title,
  pinned,
  onToggle,
}: {
  /** Stable key stored in the pins list — URL or `nav:Section`. */
  pinKey: string
  title: string
  pinned: boolean
  onToggle: (pinKey: string) => void
}) {
  return (
    <SidebarMenuAction
      showOnHover={!pinned}
      aria-label={pinned ? `Unpin ${title}` : `Pin ${title}`}
      title={pinned ? "Unpin" : "Pin"}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        onToggle(pinKey)
      }}
      className={cn(
        "right-1 top-1/2 z-10 -translate-y-1/2 group-data-[collapsible=icon]:hidden",
        "peer-data-[size=sm]/menu-button:!top-1/2 peer-data-[size=default]/menu-button:!top-1/2 peer-data-[size=lg]/menu-button:!top-1/2",
        pinned && "text-sidebar-foreground opacity-100"
      )}
    >
      <Pin className={cn("size-3.5", pinned && "fill-current")} />
    </SidebarMenuAction>
  )
}

function SortableFavoriteItem({
  item,
  pathname,
  search,
  onUnpin,
}: {
  item: NavItem
  pathname: string
  search: string
  onUnpin: (pinKey: string) => void
}) {
  const pinKey = item.pinKey ?? item.url
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: pinKey })

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : undefined,
  }

  const isActive = hasActiveDescendant(item, pathname, search)

  return (
    <SidebarMenuItem ref={setNodeRef} style={style} className="group/menu-item relative">
      <button
        type="button"
        className="absolute left-1 top-1/2 z-10 inline-flex size-5 -translate-y-1/2 cursor-grab touch-none items-center justify-center rounded-md text-sidebar-foreground/50 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:cursor-grabbing group-data-[collapsible=icon]:hidden"
        aria-label={`Reorder ${item.title}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-3.5" />
      </button>
      <SidebarMenuButton
        asChild
        tooltip={item.title}
        isActive={isActive}
        className="py-5 pl-7 pr-8 group-data-[collapsible=icon]:!p-2"
      >
        <Link to={firstNavigableUrl(item)}>
          {item.icon && <item.icon />}
          <span>{item.title}</span>
        </Link>
      </SidebarMenuButton>
      <PinButton pinKey={pinKey} title={item.title} pinned onToggle={onUnpin} />
    </SidebarMenuItem>
  )
}

function FavoritesList({
  favorites,
  pathname,
  search,
}: {
  favorites: NavItem[]
  pathname: string
  search: string
}) {
  const pins = useSidebarNavStore((s) => s.pins)
  const setPins = useSidebarNavStore((s) => s.setPins)
  const togglePin = useSidebarNavStore((s) => s.togglePin)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const ids = React.useMemo(
    () => favorites.map((f) => f.pinKey ?? f.url),
    [favorites],
  )

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = pins.indexOf(String(active.id))
    const newIndex = pins.indexOf(String(over.id))
    if (oldIndex < 0 || newIndex < 0) return
    setPins(arrayMove(pins, oldIndex, newIndex))
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <SidebarMenu>
          {favorites.map((item) => (
            <SortableFavoriteItem
              key={item.pinKey ?? item.url}
              item={item}
              pathname={pathname}
              search={search}
              onUnpin={togglePin}
            />
          ))}
        </SidebarMenu>
      </SortableContext>
    </DndContext>
  )
}

export function NavMain({
  items,
  favorites = [],
  overview = null,
}: {
  items: NavItem[]
  favorites?: NavItem[]
  overview?: NavItem | null
}) {
  const { pathname, search } = useLocation()
  const { state } = useSidebar()
  const isIcon = state === "collapsed"
  const [query, setQuery] = React.useState("")
  const togglePin = useSidebarNavStore((s) => s.togglePin)
  const pins = useSidebarNavStore((s) => s.pins)
  const pinnedSet = React.useMemo(() => new Set(pins), [pins])

  const filteredItems = React.useMemo(() => filterNavItems(items, query), [items, query])
  const filteredFavorites = React.useMemo(
    () => filterNavItems(favorites, query),
    [favorites, query]
  )
  const showOverview = React.useMemo(() => {
    if (!overview) return false
    const q = query.trim().toLowerCase()
    if (!q) return true
    return overview.title.toLowerCase().includes(q)
  }, [overview, query])
  const isFiltering = query.trim().length > 0
  const showFavorites = filteredFavorites.length > 0
  const emptySearch =
    isFiltering &&
    !showOverview &&
    filteredItems.length === 0 &&
    filteredFavorites.length === 0

  return (
    <SidebarGroup className="flex min-h-0 flex-1 flex-col gap-0 p-2">
      <div className="relative mb-2 px-0 group-data-[collapsible=icon]:hidden">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-sidebar-foreground/50" />
        <SidebarInput
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search menu…"
          className="h-8 pl-8"
          aria-label="Search menu"
        />
      </div>

      <SidebarGroupContent className="min-h-0 flex-1 overflow-auto group-data-[collapsible=icon]:[scrollbar-width:none] group-data-[collapsible=icon]:[&::-webkit-scrollbar]:hidden">
        {/* Overview is always first; Favorites (pinned) follow; then A–Z. */}
        {showOverview && overview && (
          <SidebarMenu className="mb-1">
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                tooltip={overview.title}
                isActive={leafMatches(overview.url, pathname, search)}
                className="py-5"
              >
                <Link to={overview.url}>
                  {overview.icon && <overview.icon />}
                  <span>{overview.title}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        )}

        {showFavorites && (
          <div className="mb-2">
            {!isIcon && (
              <SidebarGroupLabel className="px-2">Favorites</SidebarGroupLabel>
            )}
            <FavoritesList
              favorites={filteredFavorites}
              pathname={pathname}
              search={search}
            />
          </div>
        )}

        {!isIcon && showFavorites && (
          <SidebarGroupLabel className="px-2">All</SidebarGroupLabel>
        )}

        <SidebarMenu>
          {emptySearch && (
            <li className="px-2 py-4 text-center text-xs text-sidebar-foreground/60">
              No matches for “{query.trim()}”
            </li>
          )}

          {filteredItems.map((item) => {
            const hasChildren = Boolean(item.items && item.items.length > 0)
            const isBranchActive = hasActiveDescendant(item, pathname, search)

            if (!hasChildren) {
              const pinKey = navItemPinKey(item)
              const canPin = canPinNavItem(item)
              const pinned = canPin && pinnedSet.has(pinKey)
              return (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    tooltip={item.title}
                    isActive={leafMatches(item.url, pathname, search)}
                    className={cn("py-5", !isIcon && canPin && "pr-8")}
                  >
                    <Link to={item.url}>
                      {item.icon && <item.icon />}
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                  {!isIcon && canPin && (
                    <PinButton
                      pinKey={pinKey}
                      title={item.title}
                      pinned={pinned}
                      onToggle={togglePin}
                    />
                  )}
                </SidebarMenuItem>
              )
            }

            return (
              <TopLevelItem
                key={item.title}
                item={item}
                pathname={pathname}
                search={search}
                defaultOpen={Boolean(item.isActive || isBranchActive || isFiltering)}
                forceOpen={isFiltering}
              />
            )
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  )
}

function FlyoutNodes({
  items,
  pathname,
  search,
  depth,
}: {
  items: NavItem[]
  pathname: string
  search: string
  depth: number
}) {
  return items.map((item) => {
    const hasChildren = Boolean(item.items?.length) && depth < MAX_DEPTH
    if (!hasChildren) {
      return (
        <DropdownMenuItem key={item.title} asChild>
          <Link
            to={item.url}
            className={cn(leafMatches(item.url, pathname, search) && "bg-accent")}
          >
            {item.title}
          </Link>
        </DropdownMenuItem>
      )
    }
    return (
      <DropdownMenuSub key={item.title}>
        <DropdownMenuSubTrigger>{item.title}</DropdownMenuSubTrigger>
        <DropdownMenuSubContent className="min-w-44">
          <FlyoutNodes
            items={item.items ?? []}
            pathname={pathname}
            search={search}
            depth={depth + 1}
          />
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    )
  })
}

function TopLevelItem({
  item,
  pathname,
  search,
  defaultOpen,
  forceOpen,
}: {
  item: NavItem
  pathname: string
  search: string
  defaultOpen: boolean
  forceOpen?: boolean
}) {
  const { state } = useSidebar()
  const isBranchActive = hasActiveDescendant(item, pathname, search)
  const [open, setOpen] = React.useState(defaultOpen)
  const togglePin = useSidebarNavStore((s) => s.togglePin)
  const pins = useSidebarNavStore((s) => s.pins)
  const pinKey = navItemPinKey(item)
  const canPin = canPinNavItem(item)
  const pinned = canPin && pins.includes(pinKey)

  React.useEffect(() => {
    if (isBranchActive || forceOpen) setOpen(true)
  }, [isBranchActive, forceOpen, pathname, search])

  if (state === "collapsed") {
    return (
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton isActive={isBranchActive} className="py-5" title={item.title}>
              {item.icon && <item.icon />}
              <span>{item.title}</span>
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="start" sideOffset={8} className="min-w-48">
            <DropdownMenuLabel>{item.title}</DropdownMenuLabel>
            <FlyoutNodes
              items={item.items ?? []}
              pathname={pathname}
              search={search}
              depth={2}
            />
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    )
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen} asChild>
      <SidebarMenuItem>
        <CollapsibleTrigger asChild>
          <SidebarMenuButton
            tooltip={item.title}
            className={cn("py-5", canPin && "pr-8")}
            isActive={isBranchActive}
          >
            {item.icon && <item.icon />}
            <span>{item.title}</span>
            <ChevronRight
              className={cn(
                "ml-auto transition-transform duration-200",
                open && "rotate-90"
              )}
            />
          </SidebarMenuButton>
        </CollapsibleTrigger>
        {canPin && (
          <PinButton
            pinKey={pinKey}
            title={item.title}
            pinned={pinned}
            onToggle={togglePin}
          />
        )}
        <CollapsibleContent>
          <SidebarMenuSub>
            {item.items?.map((subItem) => (
              <NavMenuItem
                key={subItem.title}
                item={subItem}
                depth={2}
                pathname={pathname}
                search={search}
                forceOpen={forceOpen}
              />
            ))}
          </SidebarMenuSub>
        </CollapsibleContent>
      </SidebarMenuItem>
    </Collapsible>
  )
}
